import { useState, useRef, useEffect } from "react";
import { Track, ProductScheme, UserScheme } from "client-v2";
import { useDispatch, useSelector } from "react-redux";
import { actionsCreators, State } from "../../redux";
import { bindActionCreators } from "redux";

import WizardContainer, {
	WizardContainerRef,
} from "../../components/container/WizardContainer";
import Success from "../../components/section/success/Success";
import Price from "../../components/section/donation/Price";
import PaymentMethod from "../../components/section/donation/PaymentMethod";
import BirthStep from "../../components/section/Birthday";
import GenderStep from "../../components/section/Gender";
import LocationStep from "../../components/section/Location";
import VisionLeftStep from "./steps/VisionLeftStep";
import styled from "styled-components";
import { ColorV2 } from "@adoptaunabuelo/react-components";
import SuccessCrossSell from "../../components/section/success/SuccessCrossSell";

const SkipDiv = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
	color: ${ColorV2.border.neutralHigh};
	font-family: "Poppins";
	font-size: 14px;
	cursor: pointer;
	gap: 4px;
	:hover {
		text-decoration: underline;
	}
`;

const SignUpWizard = (props: Props) => {
	const dispatch = useDispatch();
	const { setShowError, setCurrentUser } = bindActionCreators(
		actionsCreators,
		dispatch
	);
	const isMobile: boolean = useSelector(
		(state: State) => state.screen.isMobile
	);
	const currentUser = useSelector((state: State) => state.user.currentUser);
	const translation = useSelector(
		(state: State) => state.location.translation
	);

	const wizard = useRef<WizardContainerRef>(null);

	const view = "register";
	const [product, setProduct] = useState<ProductScheme | undefined>(
		undefined
	);
	const [didSubscribe, setDidSubscribe] = useState<string | undefined>(
		undefined
	);

	useEffect(() => {
		//Track to Mixpanel
		Track.event({
			event: "register process init",
			user: currentUser,
			params: {
				view: view,
			},
		});
		//init
		if (currentUser) {
			initialStep(currentUser);
		}
	}, []);

	const initialStep = async (user: UserScheme) => {
		if (user) {
			if (user.name && user.surname && user.email) {
				if (user.birthday) {
					if (user.gender) {
						if (user.address) wizard.current?.setStep(6);
						else wizard.current?.setStep(5);
					} else wizard.current?.setStep(4);
				} else wizard.current?.setStep(3);
			}
		}
	};

	const renderSkipDonationButton = () => (
		<SkipDiv
			onClick={() => {
				//Track on Mixpanel
				Track.event({
					event: "skip donation modal show",
					user: currentUser,
					params: {
						view: view,
					},
				});
			}}
		>
			{translation.component_modal_skip_payment_button_skip}
		</SkipDiv>
	);

	return (
		<WizardContainer
			ref={wizard}
			views={[
				//Birthday
				{
					centralView: currentUser && (
						<BirthStep
							user={currentUser}
							onFinish={(result) => {
								if (result.error) {
									setShowError({
										show: true,
										message: result.error,
									});
								} else if (result.data) {
									setCurrentUser(result.data);
									wizard.current?.goNext();
								}
							}}
						/>
					),
				},
				//Gender
				{
					centralView: currentUser && (
						<GenderStep
							user={currentUser}
							onFinish={(result) => {
								if (result.error) {
									setShowError({
										show: true,
										message: result.error,
									});
								} else if (result.data) {
									setCurrentUser(result.data);
									wizard.current?.goNext();
								}
							}}
						/>
					),
				},
				//Location
				{
					centralView: currentUser && (
						<LocationStep
							user={currentUser}
							onFinish={(result) => {
								if (result.error) {
									setShowError({
										show: true,
										message: result.error,
									});
								} else if (result.data) {
									setCurrentUser(result.data);
									wizard.current?.goNext();
								}
							}}
						/>
					),
				},
				//Vision
				{
					centralView: (
						<VisionLeftStep
							onFinish={(result) => {
								if (result.data) {
									wizard.current?.goNext();
								}
							}}
						/>
					),
				},
				//Select price view
				{
					centralView: (
						<Price
							options={["monthly", "annually", "unique"]}
							amountSelector="selector"
							view={view}
							title={
								"Necesitamos tu ayuda para acompañar a más personas mayores"
							}
							subtitle={"Regalas {{value}} horas de compañía"}
							type_to_eur={0.602}
							subtitle_type={"horas"}
							onSkip={() => wizard.current?.goNext(2)}
							onFinish={(result) => {
								if (result.error) {
									setShowError({
										show: true,
										message: result.error,
									});
								} else if (result.data) {
									setProduct(result.data);
									wizard.current?.goNext();
								}
							}}
						/>
					),
				},
				//Select payment method
				{
					centralView:
						product && currentUser ? (
							<PaymentMethod
								product={product}
								user={currentUser}
								view={view}
								onFinish={(result) => {
									if (result.error) {
										setShowError({
											show: true,
											message: result.error,
										});
									} else if (result.data) {
										setDidSubscribe(product.charge);
										wizard.current?.goNext();
									}
								}}
								onProductChange={(result) => {
									if (result.error) {
										setShowError({
											show: true,
											message: result.error,
										});
									} else if (result.data) {
										setProduct(result.data);
									}
								}}
							/>
						) : null,
					rightView: null,
				},
				//Success view
				{
					centralView: currentUser ? (
						<Success
							user={currentUser}
							didSubscribe={didSubscribe}
							type="register"
							title="¡Ya formas parte de Adopta Un Abuelo!"
							subtitle={
								didSubscribe
									? "Ya eres parte de la gran familia que formamos los embajadores de Adopta Un Abuelo. Con vuestra ayuda estamos ayudando a que nuestros mayores recuperen el lugar tan importante que tienen en nuestra sociedad."
									: "Prepárate para disfrutar de una experiencia única en la mejor compañía"
							}
							steps={[
								{
									title: "Descarga nuestra app",
									subtitle:
										"E iniciar sesión con tu número de teléfono.",
								},
								{
									title: "Completa la formación",
									subtitle:
										"Te enseñaremos todo lo necesario para que puedas acompañar a un mayor.",
								},
								{
									title: "¡Diviértete!",
									subtitle:
										"Encontraremos al mayor ideal para ti. Podrás llamarla y visitarla para hacerle compañía.",
								},
							]}
						/>
					) : null,
					rightViewStyle: { position: "unset" },
					rightView: !isMobile ? <SuccessCrossSell /> : null,
					hideBackButton: true,
				},
			]}
		/>
	);
};

export default SignUpWizard;
export interface Props {}
